import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useGetCashFundCurrenciesQuery } from '@shared/utils/currency/cashFundCurrencies.graphql.generated';
import { buildCountryEntries, resolveCashFundLocale, DEFAULT_COUNTRY_CODE } from '@shared/utils/currency/cashFundCountries';
import type { CountryEntry } from '@shared/utils/currency/cashFundCountries';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

export type ResolvedCashFundLocale = Readonly<{
  loading: boolean;
  supported: CountryEntry | null;
  countryCode: string;
  currencyCode: string;
}>;

/**
 * Resolves a registry's configured currency to a Stripe-supported (country, currency) pair for cash funds.
 *
 * Input is the host's curated registry currency — not the raw browser locale. This is intentional:
 * `useGeoBasedLocale` has lossy fallbacks (e.g. `es-CO` silently maps to Spain/EUR via the language
 * priority chain), so deriving KYC routing from it can send a Colombian host through a Spanish
 * Stripe Connect. The registry currency, by contrast, is either explicitly set by the host or
 * auto-synced from browser locale with validation against the registry currency list (PR #15145).
 *
 * - `supported` is non-null when the registry currency maps to a BE-supported country — use this for gating UI.
 * - `countryCode` / `currencyCode` fall back to USA/USD when unsupported — use these for KYC payloads.
 */
export const useResolvedCashFundLocale = (currencyCode: string | undefined): ResolvedCashFundLocale => {
  const [isI18nEnabled] = useIsFeatureEnabled('registryCashFundCurrency18nEnabled');
  const { data, loading } = useGetCashFundCurrenciesQuery({ batchMode: 'fast', fetchPolicy: 'cache-first', skip: !isI18nEnabled });

  const entries = useMemo(() => buildCountryEntries(data?.getCashFundCurrencies.currencyDescriptors ?? []), [data]);

  const supported = useMemo(() => (isI18nEnabled ? resolveCashFundLocale(entries, currencyCode) : null), [isI18nEnabled, entries, currencyCode]);

  return {
    loading: isI18nEnabled && loading,
    supported,
    countryCode: supported?.countryCode ?? DEFAULT_COUNTRY_CODE,
    currencyCode: supported?.currencyCode ?? DEFAULT_CURRENCY,
  };
};
